// LRPO-5314 — crops a Chart.js category-axis tick label to the width the
// axis will actually be given, ellipsizing ONLY when the label genuinely
// exceeds that space. Chart.js's own default is to RAW-clip an overflowing
// label at the canvas edge (cutting the START of the word, no ellipsis) —
// the "truncated even though there's space" the ticket reports. This
// returns the FULL label whenever it fits and a clean trailing ellipsis
// otherwise. `chart.data.labels` is never mutated by this — a caller that
// needs the original value (e.g. a drilldown onClick) reads from there
// instead of the (possibly cropped) tick label.
//
// `scale.maxWidth` is the layout BUDGET Chart.js offers this axis box
// (Scale#update sets it before any tick is measured) — not the axis's FINAL
// width. Scale#fit() later sizes the box to
// `min(maxWidth, tickMarkLength + ticks.padding*2 + widestLabelWidth)`.
//
// QA re-open (2026-07-06): the first version of this crop reserved a flat
// 16px guess for that overhead. For the demographics chart's config
// (`ticks.padding: 10` -> 20px, default `grid.tickLength` 8px = 28px real
// overhead) a label cropped right up to the 16px-derived limit still didn't
// fit once fit() added its real overhead, so the tail of the ellipsis
// overlapped the bars. A flat guess cuts the other way too: any config with
// LESS overhead than assumed gets needlessly truncated even though the real
// box has room. So the reserved overhead is derived from the scale's own
// resolved ticks/grid options — the exact numbers fit() is about to use for
// this same label — instead of a hardcoded constant.
//
// QA re-open (2026-07-08, Viktor — "Secondary Product Category" chart): with a
// large layout budget (wide viewport, few competing elements) Chart.js hands
// the y-axis a very generous `maxWidth`, so an extremely long category label
// "fits" the width test and is never cropped — it then runs the full width of
// the plot and overlaps the bars/other labels.
//
// QA re-open (2026-07-08, Elmira — "truncated despite available space"): the
// prior fix answered Viktor's overrun with a flat MAX_LABEL_CHARS=24 character
// ceiling, ellipsizing ANY label over 24 chars regardless of the room the axis
// actually had (e.g. "Interior Designers & Decorators" -> "Interior Designers &
// Dec…" on a wide axis with space to spare). That is the same defect class the
// very first fix removed (server-side truncate(12)): a fixed character count
// has no relationship to available pixels, so it truncates labels that fit.
//
// Resolution — crop by REAL pixels only, but bound the pixel BUDGET. The crop
// still ellipsizes solely when a label overflows the space Chart.js allots the
// axis, so a label that fits shows in FULL. To keep Viktor's overrun fixed, the
// budget the crop measures against is capped at a fraction of the chart's own
// width (MAX_LABEL_WIDTH_FRACTION): on a wide viewport where `maxWidth` balloons,
// a very long label crops to that bounded ceiling (clean ellipsis, no bar
// overlap) instead of running the full plot; short/medium labels are untouched.
// The full value is never lost — `chart.data.labels` is not mutated, so the
// tooltip title callback (wired in the demographics partial) still shows the
// complete name on hover, and the drilldown still resolves the full raw value.
const MAX_LABEL_WIDTH_FRACTION = 0.4

// The minimum label area we will ever crop against / pin an axis to. Below this
// an axis is not a label, it is a smudge.
const MIN_LABEL_WIDTH = 40

// Custom tick option carrying the axis width agreed across ALL the demographics
// charts (see sharedLabelWidth / pinCategoryAxisWidth). Lives on the scale's
// resolved ticks options so BOTH readers — the crop below and the afterFit that
// sizes the box — take the identical number. Chart.js ignores option keys it
// does not know, and keeping it in the options tree (rather than on the scale
// instance) means it survives the update()/rebuild cycles Chartkick puts these
// remote-URL charts through.
const PINNED_WIDTH_KEY = 'sharedLabelWidthPx'

// Chart.js's own per-tick overhead for this axis: the tick mark plus the tick
// padding on both sides. Derived from the scale's RESOLVED options — the exact
// numbers Scale#fit() is about to use — never a hardcoded guess (that guess is
// what the 2026-07-06 QA re-open above was).
function axisOverhead(scale) {
  const tickOpts = (scale.options && scale.options.ticks) || {}
  const gridOpts = (scale.options && scale.options.grid) || {}
  const tickMarkLength = gridOpts.drawTicks === false
    ? 0
    : (gridOpts.tickLength != null ? gridOpts.tickLength : 8)
  return (tickOpts.padding || 0) * 2 + tickMarkLength
}

function tickFont(scale) {
  const tickOpts = (scale.options && scale.options.ticks) || {}
  const fontSize = (tickOpts.font && tickOpts.font.size) || 12
  return fontSize + "px 'Inter', system-ui, -apple-system, sans-serif"
}

// Runs fn(measure) with the canvas context set to the tick font, restoring it
// afterwards. Returns null when the chart has been torn down mid-poll.
function withTickFont(scale, fn) {
  const ctx = scale && scale.chart && scale.chart.ctx
  if (!ctx) return null
  ctx.save()
  ctx.font = tickFont(scale)
  try {
    return fn((text) => ctx.measureText(text).width)
  } finally {
    ctx.restore()
  }
}

// The widest label area this axis may occupy when it is NOT pinned: the layout
// budget Chart.js offers (scale.maxWidth), bounded to a fraction of the chart's
// own width so a single monster label cannot claim the whole canvas (Viktor's
// Mode A overrun — see the note above).
export function labelWidthCeiling(scale) {
  let budget = (scale && scale.maxWidth) || 200
  const chartWidth = (scale && scale.chart && scale.chart.width) || 0
  if (chartWidth > 0) {
    budget = Math.min(budget, chartWidth * MAX_LABEL_WIDTH_FRACTION)
  }
  return Math.max(MIN_LABEL_WIDTH, budget - axisOverhead(scale))
}

function pinnedLabelWidth(scale) {
  const tickOpts = (scale && scale.options && scale.options.ticks) || {}
  const width = tickOpts[PINNED_WIDTH_KEY]
  return typeof width === 'number' && width > 0 ? width : null
}

// The label width this axis may ACTUALLY use right now.
//
// The pin (below) is an ABSOLUTE pixel width, but Chart.js re-runs afterFit on
// every Scale#update — including `update('resize')`, and these charts are
// `responsive: true`. An unclamped pin therefore survives a viewport that has
// since got NARROWER: the axis keeps its old width, Chart.js's updateDims does
// `chartArea.w = max(0, outerWidth - axisWidth)` with no clamp of its own, and
// the plot area collapses (measured: a 332px pin agreed at a 900px column left
// a 10px plot strip once the column shrank to 380px — bars gone, no exception,
// so nothing would even surface in Honeybadger).
//
// So the pin is only ever honoured up to what the CURRENT canvas can afford —
// the same MAX_LABEL_WIDTH_FRACTION ceiling the crop already respects. Both
// readers (the crop, and the afterFit that sizes the box) go through here, so
// the ellipsis and the box can never disagree.
//
// At pin time this is a no-op: sharedLabelWidth() takes the TIGHTEST ceiling
// across the charts, so the pinned value already fits every axis. The clamp
// only bites in the window between a resize and the partial's debounced
// re-align — which is exactly the transient it exists to make safe.
export function effectiveLabelWidth(scale) {
  const ceiling = labelWidthCeiling(scale)
  const pinned = pinnedLabelWidth(scale)
  return pinned ? Math.min(pinned, ceiling) : ceiling
}

export function cropTickLabel(scale, rawLabel) {
  if (rawLabel == null) return rawLabel
  const text = String(rawLabel)
  // Chart torn down / rebuilt mid-poll: bail to the raw label rather than throw
  // inside Chart.js's tick loop (which would abort the whole axis render).
  if (!scale || !scale.chart || !scale.chart.ctx) return text

  // Crop against the PINNED shared width when the charts have agreed one
  // (LRPO-5521), else against this axis's own bounded ceiling — and in either
  // case never beyond what the current canvas can afford. Reading the same
  // number the axis box is sized to is what keeps the ellipsis and the box in
  // agreement — a crop measured against a different budget than fit() uses is
  // precisely the overlap/dead-gap class of bug this file keeps re-opening on.
  const maxPx = effectiveLabelWidth(scale)

  return withTickFont(scale, (measure) => {
    let out = text
    if (measure(text) > maxPx) {
      while (out.length > 1 && measure(out + '…') > maxPx) {
        out = out.slice(0, -1)
      }
      out += '…'
    }
    return out
  })
}

// Widest UNCROPPED category label on this axis, in px. Iterated with a plain
// loop, NOT Math.max(...labels.map(…)): a spread over a large category list is a
// "Maximum call stack size exceeded" waiting to happen (cf. LRPO-5534 on this
// same page), and these charts deliberately plot EVERY category.
export function widestLabelPx(scale) {
  const labels = (scale && scale.chart && scale.chart.data && scale.chart.data.labels) || []
  if (!labels.length) return 0

  return withTickFont(scale, (measure) => {
    let widest = 0
    for (let i = 0; i < labels.length; i++) {
      const width = measure(String(labels[i] == null ? '' : labels[i]))
      if (width > widest) widest = width
    }
    return widest
  }) || 0
}

// LRPO-5521 — "will be possible to align all charts by one line".
//
// Chart.js sizes each axis box independently, to min(maxWidth, overhead +
// widestLabelWidth) — i.e. to THAT chart's own longest label. So the
// Demographics cards, stacked one per row, each get a different y-axis width and
// their plot areas start at different x: the charts do not line up.
//
// The shared width is the widest label any of the charts actually needs, capped
// by the tightest crop ceiling among them. Deriving it from real content (rather
// than pinning a flat constant) means: a set of short-label charts aligns without
// donating 40% of every canvas to whitespace, and a chart with a monster label
// stops at the ceiling and ellipsizes — which is exactly the trade Viktor is
// describing, and why the crop has to come first for the alignment to be possible.
//
// Returns 0 when no chart has resolved its data yet — the caller must pin
// NOTHING then, rather than pinning every axis to the 40px floor.
export function sharedLabelWidth(scales) {
  const list = scales || []
  let needed = 0
  let ceiling = Infinity

  for (let i = 0; i < list.length; i++) {
    const scale = list[i]
    if (!scale) continue
    const widest = widestLabelPx(scale)
    if (widest > needed) needed = widest
    const cap = labelWidthCeiling(scale)
    if (cap < ceiling) ceiling = cap
  }

  if (needed <= 0 || !isFinite(ceiling)) return 0
  return Math.max(MIN_LABEL_WIDTH, Math.min(needed, ceiling))
}

// Pins one category axis to the shared label width, so every chart's plot area
// starts at the same x. Chart.js calls options.afterFit(scale) at the end of
// Scale#fit(), AFTER sizing the box to its own content — so overriding
// scale.width there is the sanctioned way to force a width, and it does NOT
// re-enter layout (nothing here calls update(); that is how you get the
// recursion this page is already fighting elsewhere).
//
// Idempotent: returns false when the axis is already pinned to this width, so
// the partial's 150ms poller can call it every tick without thrashing layout.
export function pinCategoryAxisWidth(scale, labelWidth) {
  if (!scale || !scale.options || !(labelWidth > 0)) return false

  const ticks = scale.options.ticks || (scale.options.ticks = {})
  if (ticks[PINNED_WIDTH_KEY] === labelWidth && typeof scale.options.afterFit === 'function') {
    return false
  }

  ticks[PINNED_WIDTH_KEY] = labelWidth
  // Chart.js re-runs afterFit on EVERY fit, resizes included — so this must
  // re-derive the width each time rather than replay a stale absolute. It reads
  // effectiveLabelWidth (never the raw pin), which clamps to the live canvas, so
  // a narrowed viewport can never collapse the plot area. Cheap and allocation-
  // free: no update() call here, so it cannot re-enter layout.
  scale.options.afterFit = function (fitted) {
    if (!pinnedLabelWidth(fitted)) return
    const width = effectiveLabelWidth(fitted)
    if (width > 0) fitted.width = width + axisOverhead(fitted)
  }
  return true
}

// LRPO-5521 — "The full name should be displayed in a tooltip when the user
// hovers over the truncated label."
//
// A Chart.js tooltip CANNOT serve this: Tooltip#_getActiveElements bails out
// when the pointer is outside chartArea (it keeps the previous actives), and a
// y-axis tick label is by definition outside it. The partial's existing tooltip
// title callback therefore only reveals the full name on hovering the BAR — not
// the truncated label the ticket names.
//
// So hit-test the axis box ourselves: given a pointer in canvas coordinates,
// return the FULL category name when it is over a tick label that is actually
// cropped, and null otherwise (nothing hidden -> nothing to reveal; over the
// bars -> leave the tooltip to Chart.js). chart.data.labels is never mutated by
// the crop, so the full value is read straight from there.
export function axisLabelAtPoint(scale, x, y) {
  if (!scale || typeof scale.getPixelForTick !== 'function') return null

  const labels = (scale.chart && scale.chart.data && scale.chart.data.labels) || []
  if (!labels.length) return null

  if (x < scale.left || x > scale.right || y < scale.top || y > scale.bottom) return null

  // Half a category row: the tick centres are evenly spaced over the axis box.
  const halfRow = (scale.height || 0) / (labels.length * 2)
  if (!(halfRow > 0)) return null

  for (let i = 0; i < labels.length; i++) {
    if (Math.abs(y - scale.getPixelForTick(i)) <= halfRow) {
      const full = String(labels[i] == null ? '' : labels[i])
      // Only when the rendered tick is actually shorter than the real name.
      return cropTickLabel(scale, full) === full ? null : full
    }
  }
  return null
}

// LRPO-5521 — the whole alignment pass, for the partial's poller: agree ONE
// label width across every resolved Demographics chart and pin each y-axis to
// it, so all the cards' plot areas start at the same x ("align all charts by
// one line"). Returns the number of axes whose pin actually CHANGED — zero on a
// steady-state tick, so the 150ms poll re-runs this for free and only calls
// update() when a newly-resolved chart moves the shared width.
export function alignCategoryAxes(charts) {
  const list = (charts || []).filter(Boolean)
  const scales = []
  for (let i = 0; i < list.length; i++) {
    const scale = list[i].scales && list[i].scales.y
    if (scale) scales.push(scale)
  }
  if (!scales.length) return 0

  const shared = sharedLabelWidth(scales)
  if (!shared) return 0

  let changed = 0
  for (let i = 0; i < list.length; i++) {
    const cjs = list[i]
    const scale = cjs.scales && cjs.scales.y
    if (!scale) continue

    if (!pinCategoryAxisWidth(scale, shared)) continue

    changed += 1

    // Mirror onto the CONFIG tree as well, so the pin survives the update() /
    // re-resolve cycles Chartkick puts these remote-URL charts through — same
    // reason the partial sets the tick callback in both places.
    //
    // ⚠️ NEVER `cfg.ticks = cfg.ticks || {}` here. `cjs.options` is a Chart.js
    // options-RESOLVER PROXY, so `cfg.ticks` is itself a proxy; assigning it
    // back onto its own parent makes it self-referential, and the next property
    // write then recurses through the proxy's set traps until the stack blows.
    // Verified against real Chart.js 4.5.1: with that line the partial's 150ms
    // poller threw `RangeError: Maximum call stack size exceeded` on its 3rd
    // tick — the same crash signature as LRPO-5534 on this very page — and
    // because the throw escapes inside the setInterval, clearInterval is never
    // reached, so the poller then throws every 150ms forever and any chart not
    // yet wired never gets its drilldown. Chart.js always materializes `ticks`
    // from its defaults, so just write straight through the resolved proxy: the
    // value lands in the raw config tree, which is the whole point of the mirror.
    // Guarded by `if (pinned)` (the `continue` above) so a steady-state poll
    // tick does not re-write it at all.
    const cfg = cjs.options && cjs.options.scales && cjs.options.scales.y
    if (cfg && cfg.ticks) {
      cfg.ticks[PINNED_WIDTH_KEY] = shared
      cfg.afterFit = scale.options.afterFit
    }

    if (typeof cjs.update === 'function') cjs.update('none')
  }
  return changed
}

// LRPO-5521 — the hover half of the ask. Chart.js cannot fire a tooltip for a
// pointer outside chartArea (see axisLabelAtPoint), so reveal the full category
// name via the canvas's native `title` while the pointer is over a truncated
// tick label. Native title = no new DOM, no positioning to get wrong, and it
// survives Turbo Frame swaps with the canvas.
//
// Guarded so the partial's poller (and a Turbo re-render) cannot stack duplicate
// listeners on the same canvas.
export function attachAxisLabelTooltip(cjs) {
  const canvas = cjs && cjs.canvas
  if (!canvas || canvas.dataset.axisLabelTooltip === 'on') return false
  canvas.dataset.axisLabelTooltip = 'on'

  const setTitle = (value) => {
    // Only write on CHANGE: re-assigning `title` on every mousemove would keep
    // resetting the browser's hover timer, so the tooltip would never appear.
    if (canvas.title !== value) canvas.title = value
  }

  canvas.addEventListener('mousemove', (event) => {
    const scale = cjs.scales && cjs.scales.y
    if (!scale) return
    const rect = canvas.getBoundingClientRect()
    const full = axisLabelAtPoint(scale, event.clientX - rect.left, event.clientY - rect.top)
    setTitle(full || '')
  })
  canvas.addEventListener('mouseleave', () => setTitle(''))
  return true
}

// Inline view <script>s cannot `import`; mirror onto window for them to reach
// (same pattern as window.leadDrill in app/frontend/lead_drill.js and
// window.reportsDayTooltipTitle in app/frontend/javascript/reports_day_tooltip.js).
if (typeof window !== 'undefined') {
  window.reportsCropTickLabel = cropTickLabel
  // LRPO-5521 — axis alignment + full-name-on-label-hover.
  window.reportsAlignCategoryAxes = alignCategoryAxes
  window.reportsAttachAxisLabelTooltip = attachAxisLabelTooltip
}
